import { makeAutoObservable, runInAction } from "mobx";

import { IS_DEV } from "@/utils/env";
import DealLensThreadTailCache from "./dealCore/DealLensThreadTailCache";
import PropertyOfferCache from "./dealCore/PropertyOfferCache";

export { DEAL_LENS_VIEWER_MISMATCH_MESSAGE } from "./dealCore/DealLensThreadTailCache";

import { mergeDealPatch } from "./dealCore/dealOfferThreadUtils";
import { mergeCurrentDealIntoPropertyOffers } from "./dealCore/propertyOfferMerge";
import { resolveDetailDealWithBootstrap as resolveDetailDealWithBootstrapUtil } from "./dealCore/resolveDetailDealWithBootstrap";
import { resolveEffectiveDealFromThreadCache } from "./dealCore/resolveEffectiveDealFromThreadCache";
import {
  deallensSwipe as deallensSwipeApi,
  fetchDealsWithProperties,
} from "./dealCore/dealsApi";
import { rowDealId, stripDeallensIdPrefix, swipeFlightKeys } from "./dealCore/deallensIdUtils";
import {
  DEALLENS_SWIPE_STATUS,
  DEALS_DEFAULT_REVALIDATE_AFTER_MS,
  DEAL_SWIPE_BUCKET,
  normalizeSwipeBucket,
} from "./dealCore/dealsConstants";
import { cachedPropertyIdMatchesKey, getCacheAge, isCacheFresh } from "./propertyCore/propertyCacheUtils";
import { ACTIVATION_STATE } from "./propertyCore/propertyApi";

function opportunitiesCacheKey(customer, userEmail, filters = {}) {
  return `${String(customer || "unknown")}::${String(userEmail || "unknown")}::${JSON.stringify(filters)}`;
}

function devLog(...args) {
  if (IS_DEV) {
    console.log(...args);
  }
}

/** Product chose 3 (initial + 2 retries) for flaky-network resilience. */
const SWIPE_PERSIST_MAX_ATTEMPTS = 3;
/** Per-attempt ceiling so a hung GraphQL call cannot leave the card spinner forever. */
const SWIPE_PERSIST_ATTEMPT_TIMEOUT_MS = 8000;
/** Safety clear if persist never reaches `finally` (e.g. HMR dropping the async chain). */
const SWIPE_IN_FLIGHT_SAFETY_CLEAR_MS =
  SWIPE_PERSIST_MAX_ATTEMPTS * SWIPE_PERSIST_ATTEMPT_TIMEOUT_MS + 2000;

function dealLastUpdatedMs(deal) {
  const raw = deal?.last_modified ?? deal?.insert_date ?? "";
  const ms = new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * Real DealLens id for deallensSwipe. Optimistic append rows must use previous_deallens_id.
 * @param {object | null | undefined} deal
 * @returns {string}
 */
function resolveSwipeDeallensId(deal) {
  const raw = String(deal?.deallens_id ?? deal?.id ?? "").trim();
  if (!raw) return "";
  if (/^optimistic:/i.test(raw)) {
    return String(deal?.metadata?.previous_deallens_id ?? "").trim();
  }
  return raw;
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("swipe persist timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

class DeallensStore {
  listCustomer = null;
  /** Effective user email from the last opportunities list load (viewer guard when set). */
  listUserEmail = null;
  listDeals = [];
  listPagination = null;
  listLoading = false;
  listLoadingMore = false;
  /**
   * User-facing load failure message shown in Alerts. Must stay fixed, sanitized copy only — never assign raw API or GraphQL error text (avoids leaking internals and keeps UI text predictable).
   * @type {string | null}
   */
  listError = null;
  listFilters = {};

  listCacheByKey = new Map();
  defaultListRevalidateAfterMs = DEALS_DEFAULT_REVALIDATE_AFTER_MS;

  _inFlightByCacheKey = new Map();
  /** @type {Map<string, Promise<object|null>>} */
  _inFlightCompleteByCacheKey = new Map();
  /** Session-level dedup (customer::email) so differing filter keys cannot race listDeals. */
  _inFlightBySessionKey = new Map();
  _inFlightListKey = null;
  _resetGeneration = 0;
  /** Deal ids that currently have a swipe request in flight (for spinner UX). */
  _swipeInFlightDealIds = {};
  /** Bumps on every swipe in-flight set/clear so card observers always re-render. */
  _swipeInFlightRevision = 0;
  /** Bumps when optimistic swipe persistence fails after retries (Opportunities hub shows a toast). */
  swipePersistenceRevertNonce = 0;
  /** Deal id for the most recent persist revert (DealLens dialog matches current route). */
  swipePersistenceRevertDealId = null;

  /** Bumps when thread-tail cache mutates (MobX tracking for detail/list UI). */
  tailRevision = 0;
  /** Bumps when property-offer cache mutates. */
  propertyOfferRevision = 0;

  constructor() {
    /** @type {DealLensThreadTailCache} */
    this._tailCache = new DealLensThreadTailCache(this);
    /** @type {PropertyOfferCache} */
    this._propertyOfferCache = new PropertyOfferCache(this);
    this.threadTails = this._tailCache.threadTails;
    makeAutoObservable(this, {
      _tailCache: false,
      _propertyOfferCache: false,
      threadTails: false,
    }, { autoBind: true });
  }

  // ── list helpers (used by cache via host reference) ──────────────────

  _syncOpportunitiesListCache() {
    const cacheKey = opportunitiesCacheKey(this.listCustomer, this.listUserEmail, this.listFilters);
    const cacheEntry = this.listCacheByKey.get(cacheKey);
    if (cacheEntry) {
      this.listCacheByKey.set(cacheKey, { ...cacheEntry, deals: this.listDeals });
    }
  }

  _invalidateActiveOpportunitiesListCache() {
    const cacheKey = opportunitiesCacheKey(this.listCustomer, this.listUserEmail, this.listFilters);
    this.listCacheByKey.delete(cacheKey);
  }

  // ── role / reset ─────────────────────────────────────────────────────

  /**
   * @param {string} role - Actual current role
   * @param {boolean} isSuperAdmin
   * @returns {string}
   */
  getDealsQueryRole(role, isSuperAdmin) {
    if (IS_DEV && isSuperAdmin) return "investor";
    return role ?? "";
  }

  reset() {
    this._resetGeneration += 1;
    this.listCustomer = null;
    this.listUserEmail = null;
    this.listDeals = [];
    this.listPagination = null;
    this.listLoading = false;
    this.listLoadingMore = false;
    this.listError = null;
    this.listFilters = {};
    this._inFlightListKey = null;
    this.listCacheByKey.clear();
    this._inFlightByCacheKey.clear();
    this._inFlightBySessionKey.clear();
    this._inFlightCompleteByCacheKey.clear();
    this._tailCache.clear();
    this._propertyOfferCache.clear();
    this._swipeInFlightDealIds = {};
    this._swipeInFlightRevision = 0;
    this.swipePersistenceRevertNonce = 0;
    this.swipePersistenceRevertDealId = null;
  }

  resetForNewCustomer() {
    this.reset();
  }

  invalidateListCache({ customer = null } = {}) {
    if (!customer) {
      this.listCacheByKey.clear();
      this._tailCache.clear();
      this._propertyOfferCache.clear();
      return;
    }

    const prefix = `${customer}::`;
    for (const key of this.listCacheByKey.keys()) {
      if (key.startsWith(prefix)) this.listCacheByKey.delete(key);
    }
    this._propertyOfferCache.clearForCustomer(customer);
    this._tailCache.clear();
  }

  // ── thread tail facade (delegates to _tailCache) ─────────────────────

  _bumpTailRevision() {
    runInAction(() => {
      this.tailRevision += 1;
    });
  }

  _bumpPropertyOfferRevision() {
    runInAction(() => {
      this.propertyOfferRevision += 1;
    });
  }

  _patchPropertyOfferFromTail(tail, opts) {
    if (tail && typeof tail === "object") {
      this._propertyOfferCache.patchFromTail(tail, opts);
    }
  }

  _threadKeyForDeal(deal, opts) {
    return this._tailCache.threadKeyForDeal(deal, opts);
  }

  getThreadTail(deal, opts) {
    return this._tailCache.getThreadTail(deal, opts);
  }

  getThreadTailForRoute(deal, rawUrlParam, routeSenderId, routeReceiverId, opts) {
    return this._tailCache.getThreadTailForRoute(deal, rawUrlParam, routeSenderId, routeReceiverId, opts);
  }

  getThreadTailEntryForRoute(deal, rawUrlParam, routeSenderId, routeReceiverId, opts) {
    return this._tailCache.getThreadTailEntryForRoute(deal, rawUrlParam, routeSenderId, routeReceiverId, opts);
  }

  registerTail(deal, opts) {
    const ok = this._tailCache.registerTail(deal, opts);
    if (ok) this._bumpTailRevision();
    return ok;
  }

  _registerTailsFromList(deals) {
    return this._tailCache.registerTailsFromList(deals);
  }

  _pruneOrphanTails() {
    return this._tailCache.pruneOrphanTails();
  }

  _mergeListDealsByThreadKey(deals) {
    return this._tailCache.mergeListByThreadKey(deals);
  }

  advanceThreadTail(deal, patch, opts) {
    const result = this._tailCache.advanceThreadTail(deal, patch, opts);
    if (result.success) {
      this._bumpTailRevision();
      // A committed server row can reuse the source deal's id, which reads as the optimistic row's predecessor.
      this._patchPropertyOfferFromTail(result.tail, { allowPredecessorReplace: opts?.kind === "draftInsert" });
    }
    return result;
  }

  revertPendingThreadTail(threadKey, opts) {
    const previousTail = this._tailCache.revertPendingThreadTail(threadKey, opts);
    if (previousTail) {
      this._bumpTailRevision();
      this._patchPropertyOfferFromTail(previousTail, { allowPredecessorReplace: true });
    }
  }

  revertPendingThreadTailForDeal(deal, opts) {
    const previousTail = this._tailCache.revertPendingForDeal(deal, opts);
    if (previousTail) {
      this._bumpTailRevision();
      this._patchPropertyOfferFromTail(previousTail, { allowPredecessorReplace: true });
    }
  }

  resolveDetailDealWithBootstrap(deal, propertyOfferBootstrap) {
    return resolveDetailDealWithBootstrapUtil(deal, propertyOfferBootstrap);
  }

  confirmPendingThreadTailForDeal(deal, opts) {
    const tail = this._tailCache.confirmPendingForDeal(deal, opts);
    if (tail) {
      this._bumpTailRevision();
      this._patchPropertyOfferFromTail(tail);
    }
    return tail;
  }

  async reconcileThreadTail(deal, opts = {}) {
    const result = await this._tailCache.reconcileThreadTail(deal, opts);
    if (result?.success && result.deal) {
      this._bumpTailRevision();
      this._patchPropertyOfferFromTail(result.deal);
    } else if (result?.reverted) {
      this._bumpTailRevision();
      const tail = this.getThreadTail(deal, {
        collectionScoped: opts.collectionScoped,
        collectionId: opts.collectionId,
      });
      this._patchPropertyOfferFromTail(tail, { allowPredecessorReplace: true });
    }
    return result;
  }

  prefetchDealLensDetail(deal) {
    return this._tailCache.prefetchDetail(deal);
  }

  peekPrefetchedDealLensDetail(rawUrlParam, routeSenderId, routeReceiverId, deal) {
    return this._tailCache.peekDetail(rawUrlParam, routeSenderId, routeReceiverId, deal);
  }

  resolveDealLensDetailForThread(opts) {
    return this._tailCache.resolveDetailForThread(opts);
  }

  // ── property offers facade ───────────────────────────────────────────

  loadPropertyOffers(params) {
    return this._propertyOfferCache.load(params);
  }

  getPropertyOffers(params) {
    return this._propertyOfferCache.getOffers(params);
  }

  /**
   * @param {object} params
   * @returns {object | null | undefined}
   */
  getThreadDeal(params) {
    void this.tailRevision;
    return resolveEffectiveDealFromThreadCache({ deallensStore: this, ...params });
  }

  promotePendingCollectionTailFromOffers(rawDeals = []) {
    if (!Array.isArray(rawDeals) || rawDeals.length === 0) return false;

    const incomingByCollection = new Map();
    for (const deal of rawDeals) {
      if (!deal || typeof deal !== "object") continue;
      const collectionId = String(deal.collection_id ?? "").trim();
      if (!collectionId) continue;
      const dealId = rowDealId(deal);
      if (!dealId || /^optimistic:/i.test(dealId)) continue;
      const prev = incomingByCollection.get(collectionId);
      if (!prev || dealLastUpdatedMs(deal) >= dealLastUpdatedMs(prev)) {
        incomingByCollection.set(collectionId, deal);
      }
    }
    if (incomingByCollection.size === 0) return false;

    const candidates = [];
    for (const [threadKey, entry] of this.threadTails.entries()) {
      if (!entry?.tail) continue;
      const tail = entry.tail;
      const tailId = rowDealId(tail);
      // Include confirmed-but-still-optimistic tips (confirmPending clears pending).
      if (!/^optimistic:/i.test(tailId)) continue;
      const collectionId = String(tail.collection_id ?? "").trim();
      if (!collectionId) continue;
      const scopedKey = this._threadKeyForDeal(tail, { collectionScoped: true, collectionId });
      if (!scopedKey || scopedKey !== threadKey) continue;
      const serverDeal = incomingByCollection.get(collectionId);
      if (!serverDeal) continue;
      const tailPropertyId = String(tail.property_id ?? "").trim();
      const serverPropertyId = String(serverDeal.property_id ?? "").trim();
      if (tailPropertyId && serverPropertyId && tailPropertyId !== serverPropertyId) continue;
      candidates.push({ tail, collectionId, serverDeal });
    }

    let promoted = false;
    for (const candidate of candidates) {
      const result = this.advanceThreadTail(
        candidate.tail,
        {},
        {
          kind: "draftInsert",
          savedRow: candidate.serverDeal,
          collectionScoped: true,
          collectionId: candidate.collectionId,
        }
      );
      if (result.success) promoted = true;
    }
    return promoted;
  }

  /**
   * @param {object} params
   * @returns {object[]}
   */
  getPropertyOffersWithCurrentDeal({
    customer,
    propertyId,
    deal,
    viewerContext = {},
    byCollection = false,
    readOfferIds = null,
  } = {}) {
    void this.propertyOfferRevision;
    const offers = this.getPropertyOffers({
      customer,
      propertyId,
      byCollection,
      viewerContext,
      readOfferIds,
    });
    return mergeCurrentDealIntoPropertyOffers(offers, deal, viewerContext);
  }

  getPropertyOfferEntry(customer, propertyId, byCollection = false, role = "") {
    return this._propertyOfferCache.getEntry(customer, propertyId, byCollection, role);
  }

  findRawPropertyOfferDeal(customer, propertyId, listId) {
    return this._propertyOfferCache.findRawDeal(customer, propertyId, listId);
  }

  patchPropertyOfferFromTail(tailDeal) {
    this._propertyOfferCache.patchFromTail(tailDeal);
  }

  // ── list filters ─────────────────────────────────────────────────────

  _listFiltersEqual(a, b) {
    const prev = a || {};
    const next = b || {};
    return (
      (prev.county ?? null) === (next.county ?? null) &&
      (prev.status ?? null) === (next.status ?? null) &&
      Boolean(prev.latestPerThread) === Boolean(next.latestPerThread)
    );
  }

  _hasWarmListSession(customer, userEmail, filters) {
    return (
      String(this.listCustomer ?? "") === String(customer ?? "") &&
      String(this.listUserEmail ?? "").trim() === String(userEmail ?? "").trim() &&
      this._listFiltersEqual(this.listFilters, filters) &&
      this.listDeals.length > 0
    );
  }

  _mergeIncomingListDeals(incomingDeals = [], { appendToCurrent = false } = {}) {
    const source = appendToCurrent ? [...this.listDeals, ...incomingDeals] : incomingDeals;
    return this._mergeListDealsByThreadKey(source);
  }

  _setListFiltersIfChanged(nextFilters) {
    const next = nextFilters || {};
    if (!this._listFiltersEqual(this.listFilters, next)) {
      this.listFilters = next;
    }
  }

  _listPaginationHasNext(pagination) {
    if (!pagination?.has_next) return false;
    const nextPage = pagination.next_page;
    return nextPage != null && nextPage !== "";
  }

  _paginationCursorKey(cursor) {
    return typeof cursor === "string" ? cursor : JSON.stringify(cursor);
  }

  // ── list load ────────────────────────────────────────────────────────

  _applyCachedList(cacheEntry, customer, filters, userEmail = null) {
    devLog("[DeallensStore] _applyCachedList", { customer, dealCount: (cacheEntry?.deals || []).length });
    const mergedDeals = this._mergeListDealsByThreadKey(cacheEntry.deals || []);
    runInAction(() => {
      this.listCustomer = customer;
      if (userEmail) this.listUserEmail = userEmail;
      this.listDeals = mergedDeals;
      this.listPagination = cacheEntry.pagination || null;
      this._setListFiltersIfChanged(filters);
      this.listLoading = false;
      this.listLoadingMore = false;
      this.listError = null;
    });
    this._registerTailsFromList(mergedDeals);
  }

  async loadList({
    customer,
    userEmail,
    queryRole,
    nextPage = null,
    reset = false,
    revalidateAfterMs = null,
    filters = {},
  } = {}) {
    devLog("[DeallensStore] loadList", { customer, nextPage, reset, filters });
    if (reset) {
      devLog("[DeallensStore] loadList background refresh (reset=true) – refetching from database");
    }
    if (!customer || !userEmail || !queryRole) {
      devLog("[DeallensStore] loadList skipped: missing customer/userEmail/queryRole");
      return null;
    }

    const normalizedNextPage = nextPage === undefined || nextPage === "" ? null : nextPage;
    const normalizedFilters = filters && typeof filters === "object" ? { ...filters } : {};
    const cacheKey = opportunitiesCacheKey(customer, userEmail, normalizedFilters);
    const cacheEntry = this.listCacheByKey.get(cacheKey) || null;
    const cacheAgeMs = cacheEntry ? getCacheAge(cacheEntry.fetchedAt) : null;
    devLog("[DeallensStore] cache check", { cacheKey, hasCache: Boolean(cacheEntry), cacheAgeMs });

    if (normalizedNextPage === null && !reset) {
      const completeInflight = this._inFlightCompleteByCacheKey.get(cacheKey);
      if (completeInflight) {
        devLog("[DeallensStore] loadList joining in-flight loadListUntilComplete");
        return completeInflight;
      }
    }

    if (normalizedNextPage === null && cacheEntry && !reset) {
      if (!this._hasWarmListSession(customer, userEmail, normalizedFilters)) {
        this._applyCachedList(cacheEntry, customer, normalizedFilters, userEmail);
      } else {
        this._syncOpportunitiesListCache();
      }
      if (isCacheFresh(cacheAgeMs, revalidateAfterMs, this.defaultListRevalidateAfterMs)) {
        devLog("[DeallensStore] loadList returning cached (fresh)");
        return {
          data: cacheEntry.deals || [],
          pagination: cacheEntry.pagination || null,
          statusCode: "cached",
          isEmpty: (cacheEntry.deals || []).length === 0,
        };
      }
    }

    const sessionKey = `${String(customer)}::${String(userEmail)}`;
    if (normalizedNextPage === null) {
      const existingPromise =
        this._inFlightByCacheKey.get(cacheKey) || this._inFlightBySessionKey.get(sessionKey);
      if (existingPromise) {
        devLog("[DeallensStore] loadList dedup: reusing in-flight promise");
        return existingPromise;
      }
    }

    const listKey = JSON.stringify({
      customer,
      queryRole,
      userEmail,
      nextPage: normalizedNextPage,
      filters: normalizedFilters,
      reset,
    });
    if (this._inFlightListKey === listKey) {
      devLog("[DeallensStore] loadList skipped: same listKey already in flight");
      return null;
    }

    devLog("[DeallensStore] loadList starting fetch");
    const generationAtStart = this._resetGeneration;
    const preserveWarmList =
      normalizedNextPage === null &&
      !reset &&
      this._hasWarmListSession(customer, userEmail, normalizedFilters);
    const runFetch = async () => {
      runInAction(() => {
        this.listCustomer = customer;
        this.listUserEmail = userEmail;
        this._setListFiltersIfChanged(normalizedFilters);
        this.listError = null;
        this._inFlightListKey = listKey;
        if (normalizedNextPage === null) {
          this.listLoading = !preserveWarmList && (!cacheEntry || reset);
        } else {
          this.listLoadingMore = true;
        }
        if (reset) {
          this.listDeals = [];
          this.listPagination = null;
        }
      });

      try {
        const apiParams = {
          customer,
          userEmail,
          queryRole,
          nextPage: normalizedNextPage,
          ...normalizedFilters,
        };

        const result = await fetchDealsWithProperties(apiParams, {
          onRaw: (rawResult) => {
            if (this._resetGeneration !== generationAtStart) return;
            if (preserveWarmList) {
              devLog("[DeallensStore] Phase 1 (raw) skipped — warm list revalidation");
              return;
            }
            const incomingDeals = Array.isArray(rawResult?.data) ? rawResult.data : [];
            devLog("[DeallensStore] Phase 1 (raw)", { incomingCount: incomingDeals.length, pagination: rawResult?.pagination });
            runInAction(() => {
              if (normalizedNextPage === null || reset) {
                this.listDeals = this._mergeIncomingListDeals(incomingDeals, {
                  appendToCurrent: preserveWarmList,
                });
              } else {
                const merged = [...this.listDeals, ...incomingDeals];
                this.listDeals = this._mergeIncomingListDeals(merged);
              }
              this.listPagination = rawResult?.pagination || null;
              this.listLoading = false;
            });
            this._registerTailsFromList(this.listDeals);
          },
        });

        if (this._resetGeneration !== generationAtStart) return null;

        const enrichedDeals = Array.isArray(result?.data) ? result.data : [];
        devLog("[DeallensStore] Phase 2 (enriched)", { count: enrichedDeals.length });

        runInAction(() => {
          if (normalizedNextPage === null || reset) {
            this.listDeals = this._mergeIncomingListDeals(enrichedDeals, {
              appendToCurrent: preserveWarmList,
            });
          } else {
            const merged = [...this.listDeals, ...enrichedDeals];
            this.listDeals = this._mergeIncomingListDeals(merged);
          }
          this.listPagination = result?.pagination ?? this.listPagination;
        });
        this._registerTailsFromList(this.listDeals);

        const fetchedAt = Date.now();
        runInAction(() => {
          this.listCacheByKey.set(cacheKey, {
            deals: this.listDeals,
            pagination: this.listPagination,
            fetchedAt,
          });
        });
        if (reset) {
          this._pruneOrphanTails();
          devLog("[DeallensStore] loadList background refresh complete", {
            dealCount: this.listDeals.length,
            cacheKey,
          });
        }

        return {
          data: result?.data || [],
          pagination: this.listPagination,
          statusCode: result?.statusCode ?? null,
          isEmpty: Boolean(result?.isEmpty),
        };
      } catch (err) {
        devLog("[DeallensStore] loadList error", { error: err, reset });
        if (reset) {
          devLog("[DeallensStore] loadList background refresh failed");
        }
        if (this._resetGeneration !== generationAtStart) return null;
        runInAction(() => {
          this.listError = "Error loading opportunities";
          if (normalizedNextPage === null) this.listDeals = [];
        });
        return null;
      } finally {
        if (this._resetGeneration === generationAtStart) {
          runInAction(() => {
            this.listLoading = false;
            this.listLoadingMore = false;
            this._inFlightListKey = null;
          });
        }
      }
    };

    const promise = runFetch();
    if (normalizedNextPage === null) {
      this._inFlightByCacheKey.set(cacheKey, promise);
      this._inFlightBySessionKey.set(sessionKey, promise);
      promise.finally(() => {
        this._inFlightByCacheKey.delete(cacheKey);
        if (this._inFlightBySessionKey.get(sessionKey) === promise) {
          this._inFlightBySessionKey.delete(sessionKey);
        }
      });
    }
    return promise;
  }

  /**
   * Fetches all paginated list pages, then publishes enriched deals once.
   * Used by `/opportunities` so cards and maps are not repainted on every page fetch.
   *
   * @param {object} [params]
   * @param {string} params.customer
   * @param {string} params.userEmail
   * @param {string} params.queryRole
   * @param {boolean} [params.reset]
   * @param {number|null} [params.revalidateAfterMs]
   * @param {object} [params.filters]
   * @returns {Promise<{ data: object[], pagination: object, statusCode: string|number|null, isEmpty: boolean } | null>}
   */
  async loadListUntilComplete({
    customer,
    userEmail,
    queryRole,
    reset = false,
    revalidateAfterMs = null,
    filters = {},
  } = {}) {
    devLog("[DeallensStore] loadListUntilComplete", { customer, reset, filters });
    if (!customer || !userEmail || !queryRole) {
      devLog("[DeallensStore] loadListUntilComplete skipped: missing customer/userEmail/queryRole");
      return null;
    }

    const normalizedFilters = filters && typeof filters === "object" ? { ...filters } : {};
    const cacheKey = opportunitiesCacheKey(customer, userEmail, normalizedFilters);
    const cacheEntry = this.listCacheByKey.get(cacheKey) || null;
    const cacheAgeMs = cacheEntry ? getCacheAge(cacheEntry.fetchedAt) : null;

    if (cacheEntry && !reset) {
      if (!this._hasWarmListSession(customer, userEmail, normalizedFilters)) {
        this._applyCachedList(cacheEntry, customer, normalizedFilters, userEmail);
      } else {
        this._syncOpportunitiesListCache();
      }
      const cachePaginationComplete = !this._listPaginationHasNext(cacheEntry.pagination);
      if (isCacheFresh(cacheAgeMs, revalidateAfterMs, this.defaultListRevalidateAfterMs) && cachePaginationComplete) {
        devLog("[DeallensStore] loadListUntilComplete returning cached (fresh, complete)");
        return {
          data: cacheEntry.deals || [],
          pagination: cacheEntry.pagination || null,
          statusCode: "cached",
          isEmpty: (cacheEntry.deals || []).length === 0,
        };
      }
    }

    const existingComplete = this._inFlightCompleteByCacheKey.get(cacheKey);
    if (existingComplete) {
      devLog("[DeallensStore] loadListUntilComplete dedup: reusing in-flight promise");
      return existingComplete;
    }

    const generationAtStart = this._resetGeneration;
    const preserveWarmList = !reset && this._hasWarmListSession(customer, userEmail, normalizedFilters);
    const completeListKey = JSON.stringify({
      customer,
      queryRole,
      userEmail,
      filters: normalizedFilters,
      reset,
      mode: "untilComplete",
    });

    const runCompleteFetch = async () => {
      const listInflight = this._inFlightByCacheKey.get(cacheKey);
      if (listInflight) {
        await listInflight.catch(() => null);
        if (this._resetGeneration !== generationAtStart) return null;
        const warmed = this.listCacheByKey.get(cacheKey);
        const warmedAge = warmed ? getCacheAge(warmed.fetchedAt) : null;
        if (
          warmed &&
          !this._listPaginationHasNext(warmed.pagination) &&
          isCacheFresh(warmedAge, revalidateAfterMs, this.defaultListRevalidateAfterMs)
        ) {
          const mergedDeals = this._mergeListDealsByThreadKey(warmed.deals || []);
          runInAction(() => {
            this.listDeals = mergedDeals;
            this.listPagination = warmed.pagination || null;
            this.listLoading = false;
            this.listLoadingMore = false;
          });
          this._registerTailsFromList(mergedDeals);
          return {
            data: mergedDeals,
            pagination: warmed.pagination || null,
            statusCode: "cached",
            isEmpty: mergedDeals.length === 0,
          };
        }
      }

      runInAction(() => {
        this.listCustomer = customer;
        this.listUserEmail = userEmail;
        this._setListFiltersIfChanged(normalizedFilters);
        this.listError = null;
        this._inFlightListKey = completeListKey;
        this.listLoading = !preserveWarmList && (!cacheEntry || reset);
        this.listLoadingMore = false;
        if (reset) {
          this.listDeals = [];
          this.listPagination = null;
        }
      });

      try {
        const seenCursors = new Set();
        let nextPage = null;
        let pagination = null;
        let statusCode = null;
        let isEmpty = true;
        const accumulated = [];

        while (true) {
          if (this._resetGeneration !== generationAtStart) return null;

          const apiParams = {
            customer,
            userEmail,
            queryRole,
            nextPage,
            ...normalizedFilters,
          };

          const result = await fetchDealsWithProperties(apiParams);
          if (this._resetGeneration !== generationAtStart) return null;

          const pageDeals = Array.isArray(result?.data) ? result.data : [];
          accumulated.push(...pageDeals);
          pagination = result?.pagination ?? null;
          statusCode = result?.statusCode ?? statusCode;
          isEmpty = Boolean(result?.isEmpty);

          if (!this._listPaginationHasNext(pagination)) break;

          const cursor = pagination.next_page;
          const cursorKey = this._paginationCursorKey(cursor);
          if (seenCursors.has(cursorKey)) break;
          seenCursors.add(cursorKey);
          nextPage = cursor;
        }

        const mergedDeals = this._mergeListDealsByThreadKey(accumulated);
        const fetchedAt = Date.now();

        runInAction(() => {
          this.listDeals = mergedDeals;
          this.listPagination = pagination;
          this.listLoading = false;
          this.listLoadingMore = false;
          this.listCacheByKey.set(cacheKey, {
            deals: mergedDeals,
            pagination,
            fetchedAt,
          });
        });
        this._registerTailsFromList(mergedDeals);
        if (reset) {
          this._pruneOrphanTails();
        }

        return {
          data: mergedDeals,
          pagination,
          statusCode,
          isEmpty,
        };
      } catch (err) {
        devLog("[DeallensStore] loadListUntilComplete error", { error: err, reset });
        if (this._resetGeneration !== generationAtStart) return null;
        runInAction(() => {
          this.listError = "Error loading opportunities";
          if (!preserveWarmList) this.listDeals = [];
          this.listLoading = false;
          this.listLoadingMore = false;
        });
        return null;
      } finally {
        if (this._resetGeneration === generationAtStart) {
          runInAction(() => {
            this._inFlightListKey = null;
          });
        }
      }
    };

    const promise = runCompleteFetch();
    this._inFlightCompleteByCacheKey.set(cacheKey, promise);
    promise.finally(() => {
      if (this._inFlightCompleteByCacheKey.get(cacheKey) === promise) {
        this._inFlightCompleteByCacheKey.delete(cacheKey);
      }
    });
    return promise;
  }

  // ── swipe / list accessors ───────────────────────────────────────────

  get likedDeals() {
    return this.listDeals.filter((deal) => normalizeSwipeBucket(deal?.swipe_status) === DEAL_SWIPE_BUCKET.LIKED);
  }

  get passedDeals() {
    return this.listDeals.filter((deal) => normalizeSwipeBucket(deal?.swipe_status) === DEAL_SWIPE_BUCKET.PASSED);
  }

  get notSeenDeals() {
    return this.listDeals.filter((deal) => normalizeSwipeBucket(deal?.swipe_status) === DEAL_SWIPE_BUCKET.OTHER);
  }

  _bumpSwipeInFlightRevision() {
    this._swipeInFlightRevision += 1;
  }

  _markSwipeInFlight(flightIds) {
    const ids = [...new Set((flightIds || []).map((id) => String(id ?? "").trim()).filter(Boolean))];
    if (ids.length === 0) return;
    const next = { ...this._swipeInFlightDealIds };
    for (const id of ids) next[id] = true;
    this._swipeInFlightDealIds = next;
    this._bumpSwipeInFlightRevision();
  }

  _clearSwipeInFlightIds(flightIds) {
    const idsToClear = [...new Set((flightIds || []).map((id) => String(id ?? "").trim()).filter(Boolean))];
    const normClear = new Set(
      idsToClear.flatMap((id) => {
        const stripped = stripDeallensIdPrefix(id);
        return stripped ? [stripped] : [];
      })
    );
    const next = { ...this._swipeInFlightDealIds };
    for (const key of Object.keys(next)) {
      if (idsToClear.includes(key)) {
        delete next[key];
        continue;
      }
      const stripped = stripDeallensIdPrefix(key);
      if (stripped && normClear.has(stripped)) delete next[key];
    }
    this._swipeInFlightDealIds = next;
    this._bumpSwipeInFlightRevision();
  }

  /**
   * @param {object | string | null | undefined} dealOrId - Deal row or a single id string (tests).
   * @returns {boolean}
   */
  isSwipeInFlight(dealOrId) {
    void this._swipeInFlightRevision;
    const keys =
      dealOrId && typeof dealOrId === "object"
        ? swipeFlightKeys(dealOrId)
        : [String(dealOrId ?? "").trim()].filter(Boolean);
    if (keys.length === 0) return false;
    return keys.some((k) => Boolean(this._swipeInFlightDealIds[k]));
  }

  async _persistSwipeWithRetries(
    deal,
    swipeStatus,
    previousSwipeStatus,
    flightIds,
    customerId,
    generationAtStart
  ) {
    const idsToClear = [...new Set((flightIds || []).map((id) => String(id ?? "").trim()).filter(Boolean))];
    const safetyTimer = setTimeout(() => {
      runInAction(() => {
        this._clearSwipeInFlightIds(idsToClear);
      });
      devLog("[DeallensStore] swipe in-flight safety clear", { dealIds: idsToClear });
    }, SWIPE_IN_FLIGHT_SAFETY_CLEAR_MS);
    const clearInFlight = () => {
      clearTimeout(safetyTimer);
      runInAction(() => {
        this._clearSwipeInFlightIds(idsToClear);
      });
    };
    const isStaleGeneration = () => this._resetGeneration !== generationAtStart;

    const deallensIdForApi = resolveSwipeDeallensId(deal);
    let lastFailure = null;

    try {
      if (!deallensIdForApi) {
        lastFailure = { reason: "missing_deallens_id" };
      } else {
        for (let attempt = 0; attempt < SWIPE_PERSIST_MAX_ATTEMPTS; attempt++) {
          if (isStaleGeneration()) return;
          if (attempt > 0) {
            await new Promise((r) => setTimeout(r, 250 * attempt));
            if (isStaleGeneration()) return;
          }
          try {
            const result = await withTimeout(
              deallensSwipeApi({
                deallensId: deallensIdForApi,
                sender: String(deal?.sender ?? "").trim(),
                receiver: String(deal?.receiver ?? "").trim(),
                swipeStatus,
                customerId,
              }),
              SWIPE_PERSIST_ATTEMPT_TIMEOUT_MS
            );
            if (isStaleGeneration()) return;
            const statusCode = Number(result?.statusCode);
            if (Number.isFinite(statusCode) && statusCode >= 200 && statusCode < 300) {
              devLog("[DeallensStore] swipeDeal persisted", {
                dealId: idsToClear[0],
                deallensIdForApi,
                swipeStatus,
                attempt,
              });
              return;
            }
            lastFailure = { statusCode };
          } catch (err) {
            lastFailure = err;
          }

          if (attempt < SWIPE_PERSIST_MAX_ATTEMPTS - 1) {
            devLog("[DeallensStore] swipe persist attempt failed, retrying", {
              dealId: idsToClear[0],
              deallensIdForApi,
              swipeStatus,
              attempt: attempt + 1,
              lastFailure,
            });
          }
        }
      }

      if (isStaleGeneration()) return;

      runInAction(() => {
        if (isStaleGeneration()) return;
        this.advanceThreadTail(deal, { swipe_status: previousSwipeStatus }, { kind: "inPlace" });
        this.swipePersistenceRevertDealId = idsToClear[0] ?? null;
        this.swipePersistenceRevertNonce += 1;
      });
      devLog("[DeallensStore] swipeDeal: persistence failed after retries, reverted optimistic update", {
        dealId: idsToClear[0],
        deallensIdForApi,
        swipeStatus,
        attempts: SWIPE_PERSIST_MAX_ATTEMPTS,
        lastFailure,
      });
    } finally {
      clearInFlight();
    }
  }

  async swipeDeal(deal, swipeStatus, { customer }) {
    const dealId = rowDealId(deal);
    const customerId = String(customer ?? "").trim();
    const previousSwipeStatus = deal?.swipe_status ?? "";
    const generationAtStart = this._resetGeneration;
    devLog("[DeallensStore] swipeDeal start (optimistic)", { dealId, swipeStatus, customerId });
    if (!dealId || !customerId) {
      devLog("[DeallensStore] swipeDeal skipped: missing dealId or customerId");
      return false;
    }

    let persistDeal = deal;
    const flightIds = swipeFlightKeys(deal);

    runInAction(() => {
      const advanced = this.advanceThreadTail(deal, { swipe_status: swipeStatus }, { kind: "inPlace" });
      if (advanced?.tail) {
        persistDeal = advanced.tail;
        for (const id of swipeFlightKeys(advanced.tail)) {
          if (!flightIds.includes(id)) flightIds.push(id);
        }
      }
      this._markSwipeInFlight(flightIds);
    });

    void this._persistSwipeWithRetries(
      persistDeal,
      swipeStatus,
      previousSwipeStatus,
      flightIds,
      customerId,
      generationAtStart
    );
    return true;
  }

  async toggleDealLike(deal, { customer }) {
    const bucket = normalizeSwipeBucket(deal?.swipe_status);
    const newStatus =
      bucket === DEAL_SWIPE_BUCKET.LIKED ? DEALLENS_SWIPE_STATUS.UNSWIPED : DEALLENS_SWIPE_STATUS.LIKED;
    devLog("[DeallensStore] toggleDealLike", { dealId: deal?.id ?? deal?.deallens_id, from: bucket, to: newStatus });
    return this.swipeDeal(deal, newStatus, { customer });
  }

  /**
   * Force LIKED (no-op if already liked). Use when engagement implies interest (e.g. send offer).
   * @param {object} deal
   * @param {{ customer: string }} opts
   * @returns {Promise<boolean>}
   */
  async setDealLiked(deal, { customer }) {
    const bucket = normalizeSwipeBucket(deal?.swipe_status);
    if (bucket === DEAL_SWIPE_BUCKET.LIKED) {
      devLog("[DeallensStore] setDealLiked skipped: already liked", {
        dealId: deal?.id ?? deal?.deallens_id,
      });
      return true;
    }
    devLog("[DeallensStore] setDealLiked", { dealId: deal?.id ?? deal?.deallens_id });
    return this.swipeDeal(deal, DEALLENS_SWIPE_STATUS.LIKED, { customer });
  }

  async setDealPassed(deal, { customer }) {
    devLog("[DeallensStore] setDealPassed", { dealId: deal?.id ?? deal?.deallens_id });
    return this.swipeDeal(deal, DEALLENS_SWIPE_STATUS.PASSED, { customer });
  }

  async setDealUnswiped(deal, { customer }) {
    devLog("[DeallensStore] setDealUnswiped", { dealId: deal?.id ?? deal?.deallens_id });
    return this.swipeDeal(deal, DEALLENS_SWIPE_STATUS.UNSWIPED, { customer });
  }

  /**
   * Marks loaded opportunity deals for a property as unavailable after deactivation.
   *
   * @param {string} propertyId
   * @returns {number} Number of live-list deals patched
   */
  markDealsInactiveForProperty(propertyId) {
    if (!propertyId) return 0;

    const matchesProperty = (deal) => {
      const dealPropertyId = deal?.property_id ?? deal?.property ?? "";
      return (
        cachedPropertyIdMatchesKey(dealPropertyId, propertyId) ||
        cachedPropertyIdMatchesKey(propertyId, dealPropertyId)
      );
    };

    const patchDeal = (deal) => {
      if (!matchesProperty(deal)) return deal;
      const nextPropertyDetails = deal.propertyDetails
        ? { ...deal.propertyDetails, active: false, activation_state: ACTIVATION_STATE.DEACTIVATED }
        : deal.propertyDetails;
      return mergeDealPatch(deal, {
        active: false,
        ...(nextPropertyDetails ? { propertyDetails: nextPropertyDetails } : {}),
      });
    };

    let updated = 0;
    runInAction(() => {
      this.listDeals = this.listDeals.map((deal) => {
        const next = patchDeal(deal);
        if (next !== deal) updated += 1;
        return next;
      });

      for (const [cacheKey, cacheEntry] of this.listCacheByKey.entries()) {
        if (!Array.isArray(cacheEntry?.deals)) continue;
        this.listCacheByKey.set(cacheKey, {
          ...cacheEntry,
          deals: cacheEntry.deals.map(patchDeal),
        });
      }

      this._tailCache.invalidateDetailCacheForProperty(propertyId);
    });

    return updated;
  }

  patchListDeal(deal, patch) {
    if (!deal || !patch || typeof patch !== "object") return false;

    const threadKey = this._threadKeyForDeal(deal);
    if (!threadKey) {
      const dealId = rowDealId(deal);
      if (!dealId) return false;
      let updated = false;
      runInAction(() => {
        this.listDeals = this.listDeals.map((d) => {
          if (rowDealId(d) !== dealId) return d;
          updated = true;
          return mergeDealPatch(d, patch);
        });
        if (updated) this._syncOpportunitiesListCache();
      });
      return updated;
    }

    const result = this.advanceThreadTail(deal, patch, { kind: "inPlace" });
    return result.success;
  }
}

export default DeallensStore;
